
import { useState, useCallback } from 'react';
import { paymentTrackingService } from '@/services/payments/paymentTrackingService';
import { PaymentStatusChange } from '@/types/communication';
import { useStudents } from '@/contexts';

export const usePaymentStatusTracking = (studentId?: number) => {
  const [history, setHistory] = useState<PaymentStatusChange[]>([]);
  const [loading, setLoading] = useState(false);
  const { getStudent } = useStudents();
  
  const loadHistory = useCallback(async () => {
    if (!studentId) return;
    
    setLoading(true);
    try {
      const paymentHistory = await paymentTrackingService.getPaymentStatusHistory(studentId);
      setHistory(paymentHistory);
    } catch (error) {
      console.error('Error loading payment history:', error);
    } finally {
      setLoading(false);
    }
  }, [studentId]);
  
  const handleStatusChange = useCallback(async (
    previousStatus: 'pending' | 'partial' | 'paid',
    newStatus: 'pending' | 'partial' | 'paid',
    amount?: number,
    programId?: string,
    notes?: string
  ): Promise<void> => {
    if (!studentId) return;
    
    try {
      // If programId is not provided, try to get the first program from the student
      if (!programId) {
        const student = getStudent(studentId);
        programId = student?.programs[0]?.title || 'Unknown Program';
      }
      
      await paymentTrackingService.trackStatusChange({
        studentId,
        programId,
        previousStatus,
        newStatus,
        amount,
        changedBy: 'Administrator', // Default value
        notes
      });
      
      // Refresh history
      await loadHistory();
    } catch (error) {
      console.error('Error handling payment status change:', error);
    }
  }, [studentId, loadHistory, getStudent]);
  
  return {
    history,
    loading,
    loadHistory,
    handleStatusChange
  };
};
